/**
 * FE seed mirror for offline/degraded scene resolve (resolver only — content from API preferred).
 */
export { resolveGuideBridge, CONNECTION_GUIDES_MAP } from '../connectionGuidesMap';

/**
 * Match routePrefixes that may include ?query (settings tabs, etc.).
 * Longer / more specific prefixes win in the caller (score by prefix length).
 */
export function matchRoutePrefix(pathWithSearch, prefix) {
  const rawPath = String(pathWithSearch || '/');
  const rawPrefix = String(prefix || '').trim();
  if (!rawPrefix) return false;

  const split = (value) => {
    const qi = String(value).indexOf('?');
    if (qi === -1) return { name: String(value), search: '' };
    return { name: String(value).slice(0, qi), search: String(value).slice(qi + 1) };
  };

  const path = split(rawPath);
  const pref = split(rawPrefix);
  // Exact `/` must not match every App route
  if (pref.name === '/') {
    return path.name === '/' || path.name === '';
  }
  const nameOk = path.name === pref.name || path.name.startsWith(`${pref.name}/`);
  if (!nameOk) return false;

  if (!pref.search) return true;

  const need = new URLSearchParams(pref.search);
  const have = new URLSearchParams(path.search);
  for (const [key, value] of need.entries()) {
    if (have.get(key) !== value) return false;
  }
  return true;
}

export function pickSceneByPath(scenes, pathname) {
  const path = String(pathname || '/');
  let best = null;
  let score = -1;
  for (const scene of scenes || []) {
    for (const prefix of scene.routePrefixes || []) {
      if (!prefix) continue;
      if (matchRoutePrefix(path, prefix)) {
        const s = String(prefix).length;
        if (s > score) {
          best = scene;
          score = s;
        }
      }
    }
  }
  return best;
}

/** Type priority for resolver (higher wins on equal route length). */
export const SCENE_TYPE_PRIORITY = {
  first_visit: 100,
  onboarding: 80,
  flow: 70,
  hint: 60,
  module_context: 40,
  guide: 20,
};

/**
 * Resolve active scene with first_visit once-semantics + longest route prefix.
 * When setup-health is 100%, suppress incomplete onboarding/flow copy (Guide↔Hub sync).
 */
export function resolveCompanionScene(list, pathname, health, dismissed, completedFlows) {
  const path = pathname || '/';
  const dismissedSet = new Set(dismissed || []);
  const completedSet = new Set(completedFlows || []);
  const percent = typeof health?.overall_percent === 'number' ? health.overall_percent : null;
  const setupComplete = percent != null && percent >= 100;
  let best = null;
  let bestScore = -1;

  for (const scene of list || []) {
    if (!scene?.id) continue;
    if (dismissedSet.has(scene.id)) continue;
    if (scene.type === 'first_visit' && completedSet.has(scene.id)) continue;
    if (scene.type === 'flow' && completedSet.has(scene.id)) continue;

    // At 100%: no «начнём настройку» / storefront step / Hub flow lock / first-visit onboarding
    if (setupComplete) {
      if (scene.id === 'onboarding_first_login') continue;
      if (scene.id === 'hint_setup_incomplete') continue;
      if (scene.id === 'flow_commerce_hub') continue;
      if (scene.id === 'onboarding_storefront_live') continue;
      if (scene.type === 'onboarding' && scene.id !== 'onboarding_complete') continue;
      if (scene.type === 'first_visit') continue;
    } else if (scene.id === 'onboarding_complete') {
      continue;
    }

    const prefixes = scene.routePrefixes || [];
    if (!prefixes.length) continue;

    for (const prefix of prefixes) {
      if (!prefix) continue;
      if (matchRoutePrefix(path, prefix)) {
        const typeBoost = SCENE_TYPE_PRIORITY[scene.type] || 0;
        // Prefer completion guide over generic module_context on Hub/dashboard
        const completeBoost = setupComplete && scene.id === 'onboarding_complete' ? 50 : 0;
        const score = String(prefix).length * 10 + typeBoost + completeBoost;
        if (score > bestScore) {
          best = scene;
          bestScore = score;
        }
      }
    }
  }

  // Hub checklist: prefer setup-health next_action over first_visit / flow chips
  // (e.g. do not show «Шаг 6 · витрина» when next is company tax / agreements).
  const pathName = String(path || '/').split('?')[0];
  if (!setupComplete && pathName === '/commerce-hub' && health?.next_action?.scene_id) {
    const nextScene = (list || []).find((s) => s.id === health.next_action.scene_id && !dismissedSet.has(s.id));
    if (nextScene) best = nextScene;
  }

  // next_action scene only if it actually applies to this path — otherwise
  // /company/create stole Price Hub chip and confused the Guide (hang UX).
  if (!best && !setupComplete && health?.next_action?.scene_id) {
    const candidate = (list || []).find((s) => s.id === health.next_action.scene_id) || null;
    if (candidate) {
      const prefixes = candidate.routePrefixes || [];
      const applies = !prefixes.length
        || prefixes.some((prefix) => matchRoutePrefix(path, prefix));
      if (applies) best = candidate;
    }
  }
  if (!best && setupComplete && isHomeLikePath(path)) {
    best = (list || []).find((s) => s.id === 'onboarding_complete' && !dismissedSet.has(s.id)) || null;
  }
  if (!best && isHomeLikePath(path)) {
    best = (list || []).find((s) => s.id === 'onboarding_first_login' && !dismissedSet.has(s.id)) || null;
  }
  return best;
}

function isHomeLikePath(pathname) {
  const p = String(pathname || '/').split('?')[0];
  return p === '/' || p === '/dashboard' || p === '/commerce-hub';
}
